import React, { useMemo } from 'react';

import { DropdownButton, Label } from '@ergeon/core-components';
import { UsersIcon, UserIcon } from '@ergeon/icons/hero/micro';

import { buildEmailOptions, buildDisplayNameOptions, isDivider } from '../utils/senderOptions';

type SenderSelectorProps = {
  senderEmail: string;
  displayName: string;
  personalEmail: string;
  personalName: string;
  onSenderEmailChange: (email: string) => void;
  onDisplayNameChange: (name: string) => void;
  disabled?: boolean;
};

const SenderSelector: React.FC<SenderSelectorProps> = ({
  senderEmail,
  displayName,
  personalEmail,
  personalName,
  onSenderEmailChange,
  onDisplayNameChange,
  disabled = false,
}) => {
  // Build email dropdown items from business logic
  const emailItems = useMemo(() => {
    const options = buildEmailOptions(personalEmail);

    return options.map((option) => {
      if (isDivider(option)) {
        return { type: 'Divider' as const };
      }
      return {
        label: option.label,
        onClick: () => onSenderEmailChange(option.value),
        selected: senderEmail === option.value,
        icon: option.isPersonal ? <UserIcon className="size-4" /> : <UsersIcon className="size-4" />,
      };
    });
  }, [personalEmail, senderEmail, onSenderEmailChange]);

  // Build display name dropdown items from business logic
  const displayNameItems = useMemo(() => {
    const options = buildDisplayNameOptions(personalName);

    return options.map((option) => {
      if (isDivider(option)) {
        return { type: 'Divider' as const };
      }
      return {
        label: option.label,
        onClick: () => onDisplayNameChange(option.value),
        selected: displayName === option.value,
        icon: option.isPersonal ? <UserIcon className="size-4" /> : <UsersIcon className="size-4" />,
      };
    });
  }, [personalName, displayName, onDisplayNameChange]);

  // Get the selected label to display on button
  const selectedEmailLabel = senderEmail || 'Select email...';
  const selectedDisplayNameLabel = displayName || 'Select name...';

  return (
    <div className="flex items-center gap-2">
      <Label className="shrink-0">From:</Label>
      <DropdownButton
        alignment="left"
        disabled={disabled}
        flavor="regular"
        hideChevron={false}
        items={emailItems}
        label={selectedEmailLabel}
        size="small"
        taste="boundless"
        title="Select sender email address" />
      <Label className="shrink-0">As:</Label>
      <DropdownButton
        alignment="left"
        disabled={disabled}
        flavor="regular"
        hideChevron={false}
        items={displayNameItems}
        label={selectedDisplayNameLabel}
        size="small"
        taste="boundless"
        title="Select display name" />
    </div>
  );
};

export default SenderSelector;
